import { computed, effect, inject, Injectable } from '@angular/core';
import {
  CreateExpertManagerRequest,
  KeycloakRealm,
  UpdateUsernameRequest,
  UpdateUserNicknameRequest,
  UserRole,
  WebColorScheme,
} from '@libs/shared/api';
import { AuthUserProfile } from '@sprint-expert-manager/app/core/auth/auth-user-profile';
import { AppStatusCoreStore } from '@sprint-expert-manager/app/core/state/store/app-status-core.store';
import { AuthUserProfileCoreStore } from '@sprint-expert-manager/app/core/state/store/auth-user-profile-core.store';
import { CurrentExpertManagerCoreStore } from '@sprint-expert-manager/app/core/state/store/current-expert-manager-core-store.service';
import { CurrentUserCoreStore } from '@sprint-expert-manager/app/core/state/store/current-user-core.store';
import {
  ExpertManagerEventNotificationCoreQueryFilter,
  ExpertManagerEventNotificationCoreStore,
} from '@sprint-expert-manager/app/core/state/store/expert-manager-event-notification-core.store';
import { NavigationItemCoreStore } from '@sprint-expert-manager/app/core/state/store/navigation-item-core.store';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  readonly #authUserProfileCoreStore = inject(AuthUserProfileCoreStore);
  readonly #currentUserCoreStore = inject(CurrentUserCoreStore);
  readonly #currentExpertManagerCoreStore = inject(
    CurrentExpertManagerCoreStore
  );
  readonly #appStatusCoreStore = inject(AppStatusCoreStore);
  readonly #navigationItemCoreStore = inject(NavigationItemCoreStore);
  readonly #eventNotificationCoreStore = inject(
    ExpertManagerEventNotificationCoreStore
  );

  authUserProfile$$ = this.#authUserProfileCoreStore.content$$;
  authUserProfileIsLoading$$ = this.#authUserProfileCoreStore.isLoading$$;
  authUserProfileIsLoaded$$ = this.#authUserProfileCoreStore.isLoaded$$;
  authUserProfileErrorMessage$$ = this.#authUserProfileCoreStore.errorMessage$$;
  currentUserRole$$ = computed(() => {
    if (this.authUserProfile$$() && this.authUserProfileIsLoaded$$()) {
      const roles = this.authUserProfile$$().info.realm_access.roles;
      if (roles.includes('ROLE_EXPERT_MANAGER')) {
        return UserRole.ExpertManager;
      } else if (roles.includes('ROLE_PREP_EXPERT_MANAGER')) {
        return UserRole.PrepExpertManager;
      }
    }
  });

  currentUser$$ = this.#currentUserCoreStore.content$$;
  currentUserIsLoading$$ = this.#currentUserCoreStore.isLoading$$;
  currentUserIsLoaded$$ = this.#currentUserCoreStore.isLoaded$$;
  currentUserErrorMessage$$ = this.#currentUserCoreStore.errorMessage$$;

  currentExpertManager$$ = this.#currentExpertManagerCoreStore.content$$;
  currentExpertManagerIsLoading$$ =
    this.#currentExpertManagerCoreStore.isLoading$$;
  currentExpertManagerIsLoaded$$ =
    this.#currentExpertManagerCoreStore.isLoaded$$;
  currentExpertManagerErrorMessage$$ =
    this.#currentExpertManagerCoreStore.errorMessage$$;

  appStatus$$ = this.#appStatusCoreStore.content$$;
  appStatusIsLoading$$ = this.#appStatusCoreStore.isLoading$$;
  appStatusIsLoaded$$ = this.#appStatusCoreStore.isLoaded$$;
  appStatusErrorMessage$$ = this.#appStatusCoreStore.errorMessage$$;

  navigationItems$$ = this.#navigationItemCoreStore.content$$;
  navigationItemsIsLoading$$ = this.#navigationItemCoreStore.isLoading$$;
  navigationItemsIsLoaded$$ = this.#navigationItemCoreStore.isLoaded$$;
  navigationItemsErrorMessage$$ = this.#navigationItemCoreStore.errorMessage$$;

  eventNotificationSlice$$ = this.#eventNotificationCoreStore.slice$$;
  eventNotificationPageQuery$$ = this.#eventNotificationCoreStore.pageQuery$$;
  eventNotificationIsLoading$$ = this.#eventNotificationCoreStore.isLoading$$;
  eventNotificationIsLoaded$$ = this.#eventNotificationCoreStore.isLoaded$$;
  eventNotificationErrorMessage$$ =
    this.#eventNotificationCoreStore.errorMessage$$;
  eventNotificationUnreadCount$$ =
    this.#eventNotificationCoreStore.unreadCount$$;

  expertManagerCreated$ = this.#currentExpertManagerCoreStore.created$;
  expertManagerUpdated$ = this.#currentExpertManagerCoreStore.updated$;

  constructor() {
    effect(
      () => {
        if (this.authUserProfile$$()) {
          this.#currentUserCoreStore.createUserByAuthUserProfile({
            authUserProfile: this.authUserProfile$$(),
            realm: KeycloakRealm.SprintExpertManagerApp,
          });

          this.#navigationItemCoreStore.fetchNavigationItems({
            userRole: this.currentUserRole$$(),
          });

          this.#currentExpertManagerCoreStore.fetchExpertManagerById({
            id: this.authUserProfile$$().info.sub,
          });
        }
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  enter() {
    this.fetchAppStatus();
  }

  fetchAppStatus() {
    this.#appStatusCoreStore.fetchAppStatus();
  }

  setAuthUser(authUserProfile: AuthUserProfile) {
    this.#authUserProfileCoreStore.setAuthUserProfile(authUserProfile);
  }

  createCurrentExpertManager(request: CreateExpertManagerRequest) {
    this.#currentExpertManagerCoreStore.createExpertManager({
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateWebColorScheme(webColorScheme: WebColorScheme) {
    this.#currentExpertManagerCoreStore.updateExpertManagerWebColorScheme({
      id: this.currentExpertManager$$().id,
      webColorScheme,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateCurrentUserNickname(request: UpdateUserNicknameRequest) {
    this.#currentUserCoreStore.updateUserNickname({
      id: this.currentUser$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateCurrentUsername(request: UpdateUsernameRequest) {
    this.#currentUserCoreStore.updateUsername({
      id: this.currentUser$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateCurrentUserProfileImage(imageFile: Blob, contentType: string) {
    this.#currentUserCoreStore.updateUserProfileImage({
      id: this.currentUser$$().id,
      imageFile,
      contentType,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  deleteCurrentUserProfileImage() {
    this.#currentUserCoreStore.deleteUserProfileImage({
      id: this.currentUser$$().id,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  fetchExpertManagerEventNotificationSlice() {
    this.#eventNotificationCoreStore.fetchExpertManagerEventNotificationSlice({
      active: true,
    });
  }

  fetchNextExpertManagerEventNotificationSlice() {
    this.#eventNotificationCoreStore.fetchNextExpertManagerEventNotificationSlice();
  }

  fetchExpertManagerEventNotificationById(id: string) {
    this.#eventNotificationCoreStore.fetchExpertManagerEventNotificationById({
      id,
    });
  }

  fetchExpertMangerEventNotificationUnreadCount() {
    this.#eventNotificationCoreStore.fetchExpertMangerEventNotificationUnreadCount();
  }

  activateExpertManagerEventNotification(id: string) {
    this.#eventNotificationCoreStore.activateExpertManagerEventNotification({
      id,
    });
  }

  inactivateExpertManagerEventNotification(id: string) {
    this.#eventNotificationCoreStore.inactivateExpertManagerEventNotification({
      id,
    });
  }

  readExpertManagerEventNotification(id: string) {
    this.#eventNotificationCoreStore.readExpertManagerEventNotification({
      id,
    });
  }

  unreadExpertManagerEventNotification(id: string) {
    this.#eventNotificationCoreStore.unreadExpertManagerEventNotification({
      id,
    });
  }

  readAllExpertManagerEventNotifications(notifierId: string) {
    this.#eventNotificationCoreStore.readAllExpertManagerEventNotifications({
      request: {
        notifierId,
      },
    });
  }

  updateExpertManagerEventNotificationPageQuery(
    pageQuery: ExpertManagerEventNotificationCoreQueryFilter
  ) {
    this.#eventNotificationCoreStore.updateExpertManagerEventNotificationPageQuery(
      pageQuery
    );
  }
}

export function injectCoreFeature() {
  return inject(CoreService);
}
